import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchHistory } from '../../lib/supabase-db';
import { supabase } from '../../config/supabase';
import HistoryItem from '../HistoryItem';

interface HistoryItemData {
  id: string;
  title: string;
}

interface HistoryProps {
  isCollapsed?: boolean;
}

export const History: React.FC<HistoryProps> = ({ isCollapsed = false }) => {
  const [historyItems, setHistoryItems] = useState<HistoryItemData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();

  // Get the active conversation ID from URL query params
  const activeConversationId = searchParams.get('conversation');

  // Fetch history on component mount
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const { data, error } = await fetchHistory();
        if (error) {
          console.error('Failed to load history:', error);
        } else {
          setHistoryItems(data);
        }
      } catch (error) {
        console.error('Error loading history:', error);
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, []);

  // Track if we've already loaded history to avoid unnecessary reloads
  const [hasLoaded, setHasLoaded] = useState(false);

  // Update loading state when we have data
  useEffect(() => {
    if (historyItems.length > 0 && !hasLoaded) {
      setHasLoaded(true);
      setLoading(false);
    }
  }, [historyItems, hasLoaded]);

  // Handle conversation update (rename)
  const handleConversationUpdate = (id: string, newTitle: string) => {
    setHistoryItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, title: newTitle } : item)),
    );
  };

  // Handle conversation delete
  const handleConversationDelete = (id: string) => {
    setHistoryItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Handle new conversation creation from Agent component
  useEffect(() => {
    const handleConversationCreated = (event: CustomEvent) => {
      const { id, title } = event.detail;

      // Add new conversation to the beginning of the list
      setHistoryItems((prev) => [{ id, title }, ...prev]);

      // Set loading to false since we just added the conversation locally
      setLoading(false);
      setHasLoaded(true);
    };

    // Add event listener
    window.addEventListener('conversationCreated', handleConversationCreated as EventListener);

    // Cleanup
    return () => {
      window.removeEventListener('conversationCreated', handleConversationCreated as EventListener);
    };
  }, []);

  // Handle real-time conversation title updates
  useEffect(() => {
    let channel: any;

    const setupRealtimeSubscription = () => {
      channel = supabase
        .channel('conversation-title-updates')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'conversations',
            filter: 'title_generated=eq.true',
          },
          (payload: any) => {
            console.log('[History] Title update received:', payload);

            if (payload.new && payload.old && payload.new.title !== payload.old.title) {
              // Update the conversation title in the local state
              handleConversationUpdate(payload.new.id, payload.new.title);
            }
          },
        )
        .subscribe((status: any) => {
          if (status === 'SUBSCRIBED') {
            console.log('[History] Realtime subscription active for title updates');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('[History] Realtime subscription error');
          }
        });
    };

    setupRealtimeSubscription();

    // Cleanup function
    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, []); // Empty dependency array - run only once
  if (isCollapsed) {
    return null; // Don't show history when collapsed
  }

  return (
    <>
      {/* History Header */}
      <div className="px-6 overflow-x-hidden pb-3 mt-10">
        <h3 className="text-[#989797] font-bold text-sm">History</h3>
      </div>

      {/* History Items */}
      <div className="flex-1 px-6 overflow-y-auto overflow-x-hidden w-full">
        {loading ? (
          <div className="py-2">
            <div className="text-gray-400 text-xs">Loading...</div>
          </div>
        ) : historyItems.length > 0 ? (
          <>
            {historyItems.map((item) => (
              <HistoryItem
                key={item.id}
                id={item.id}
                title={item.title}
                isActive={activeConversationId === item.id}
                onUpdate={handleConversationUpdate}
                onDelete={handleConversationDelete}
              />
            ))}
          </>
        ) : (
          <div className="py-2">
            <div className="text-gray-400 text-xs">No conversations yet</div>
          </div>
        )}
      </div>
    </>
  );
};
